import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { staffService } from '../../services/staffService';
import { FiSave, FiX, FiPlus, FiTrash2 } from 'react-icons/fi';

const StaffForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    skills: [],
    level: 'Junior',
    commission: 5,
    status: 'active',
    joinDate: new Date().toISOString().split('T')[0],
    notes: '',
	photo: ''
  });

  const [newSkill, setNewSkill] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const skillOptions = [
    'Fabrikasi Logam',
    'Pengelasan SMAW, GMAW, GTAW',
	'Konstruksi Baja',
    'Piping',
    'Bubut',
    'CNC',
	'Teknik Bangunan',
    'Instalasi Listrik',
    'Instalasi Jaringan',
    'Servis Elektronik',
    'Diagnosis Mesin',
    'Perbaikan Kendaraan R2',
	'Perbaikan Kendaraan R4',
    'Quality Control',
    'Project Management'
  ];

  const levelOptions = [
    { value: 'Junior', label: 'Junior' },
    { value: 'Middle', label: 'Middle' },
    { value: 'Senior', label: 'Senior' },
    { value: 'Lead', label: 'Lead / Supervisor' },
    { value: 'Manager', label: 'Manager' }
  ];

  const statusOptions = [
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
    { value: 'on-leave', label: 'On Leave' }
  ];

  useEffect(() => {
  const init = async () => {
    if (isEdit) {
      await loadStaff();
    }
  };
  init();
}, [id]);

const loadStaff = async () => {
  try {
    const staff = await staffService.getById(id);
    if (staff) {
      setFormData({
        name: staff.name || '',
        phone: staff.phone || '',
        email: staff.email || '',
        skills: staff.skills || [],
        level: staff.level || 'Junior',
        commission: staff.commission || 5,
        status: staff.status || 'active',
        joinDate: staff.joinDate || new Date().toISOString().split('T')[0],
        notes: staff.notes || '',
		photo: staff.photo || ''
      });
    } else {
      setError('Staff not found');
    }
  } catch (error) {
    console.error('Error loading staff:', error);
    setError('Gagal memuat data staff');
  }
};
  

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAddSkill = () => {
    if (newSkill.trim() && !formData.skills.includes(newSkill.trim())) {
      setFormData(prev => ({
        ...prev,
        skills: [...prev.skills, newSkill.trim()]
      }));
      setNewSkill('');
    }
  };

  const handleRemoveSkill = (skillToRemove) => {
    setFormData(prev => ({
      ...prev,
      skills: prev.skills.filter(skill => skill !== skillToRemove)
    }));
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddSkill();
    }
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      setError('Nama staff wajib diisi');
      return false;
    }
    if (!formData.phone.trim()) {
      setError('Nomor telepon wajib diisi');
      return false;
    }
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setError('Format email tidak valid');
      return false;
    }
    if (formData.skills.length === 0) {
      setError('Minimal 1 skill harus diisi');
      return false;
    }
    if (formData.commission < 0 || formData.commission > 100) {
      setError('Komisi harus antara 0-100%');
      return false;
    }
    return true;
  };

  // Di StaffForm.jsx, bagian handleSubmit
const handleSubmit = async (e) => {
  e.preventDefault();
  setError('');
  
  if (!validateForm()) return;

  setLoading(true);
  try {
    if (isEdit) {
      await staffService.update(id, formData);
    } else {
      await staffService.create(formData);
    }
    navigate('/staff');
  } catch (error) {
    console.error('Error saving staff:', error);
    const errorMessage = error?.message || error?.toString() || 'Terjadi kesalahan saat menyimpan';
    setError(errorMessage);
  } finally {
    setLoading(false);
  }
};

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-[var(--text-primary)]">
          {isEdit ? 'Edit Staff' : 'New Staff'}
        </h1>
        <button
          onClick={() => navigate('/staff')}
          className="flex items-center space-x-2 px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg transition-colors"
        >
          <FiX size={20} />
          <span>Cancel</span>
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="bg-[var(--bg-primary)] p-6 rounded-xl shadow-[var(--shadow-sm)] border border-[var(--border-color)]">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1">
              Nama Staff *
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Contoh: Ahmad Firmansyah"
              className="w-full px-3 py-2 border border-[var(--border-color)] rounded-lg bg-[var(--bg-primary)] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1">
              Nomor Telepon *
            </label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="Contoh: 08123456788"
              className="w-full px-3 py-2 border border-[var(--border-color)] rounded-lg bg-[var(--bg-primary)] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1">
              Email
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Contoh: staff@eos7.com"
              className="w-full px-3 py-2 border border-[var(--border-color)] rounded-lg bg-[var(--bg-primary)] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1">
              Level / Jabatan
            </label>
            <select
              name="level"
              value={formData.level}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-[var(--border-color)] rounded-lg bg-[var(--bg-primary)] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {levelOptions.map(level => (
                <option key={level.value} value={level.value}>
                  {level.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1">
              Komisi (%)
            </label>
            <input
              type="number"
              name="commission"
              value={formData.commission}
              onChange={handleChange}
              min="0"
              max="100"
              placeholder="Contoh: 10"
              className="w-full px-3 py-2 border border-[var(--border-color)] rounded-lg bg-[var(--bg-primary)] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1">
              Status
            </label>
            <select
              name="status"
              value={formData.status}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-[var(--border-color)] rounded-lg bg-[var(--bg-primary)] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {statusOptions.map(status => (
                <option key={status.value} value={status.value}>
                  {status.label}
                </option>
              ))}
            </select>
          </div>
		  
		  <div className="md:col-span-2">
  <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1">
    Foto URL
  </label>
  <input
    type="text"
    name="photo"
    value={formData.photo || ''}
    onChange={handleChange}
    placeholder="https://ui-avatars.com/api/?name=Nama+Staff&background=3b82f6&color=fff&size=128"
    className="w-full px-3 py-2 border border-[var(--border-color)] rounded-lg bg-[var(--bg-primary)] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-blue-500"
  />
  <p className="text-xs text-[var(--text-secondary)] mt-1">
    Masukkan URL gambar profile
  </p>
</div>

          <div>
            <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1">
              Tanggal Bergabung
            </label>
            <input
              type="date"
              name="joinDate"
              value={formData.joinDate}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-[var(--border-color)] rounded-lg bg-[var(--bg-primary)] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1">
              Skills / Keahlian *
            </label>
            <div className="flex space-x-2">
              <input
                type="text"
                value={newSkill}
                onChange={(e) => setNewSkill(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Tambahkan skill..."
                className="flex-1 px-3 py-2 border border-[var(--border-color)] rounded-lg bg-[var(--bg-primary)] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-blue-500"
                list="skillSuggestions"
              />
              <datalist id="skillSuggestions">
                {skillOptions.map(skill => (
                  <option key={skill} value={skill} />
                ))}
              </datalist>
              <button
                type="button"
                onClick={handleAddSkill}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
              >
                <FiPlus size={20} />
              </button>
            </div>

            <div className="mt-2 flex flex-wrap gap-2">
              {formData.skills.map((skill) => (
                <span
                  key={skill}
                  className="inline-flex items-center space-x-1 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm"
                >
                  <span>{skill}</span>
                  <button
                    type="button"
                    onClick={() => handleRemoveSkill(skill)}
                    className="hover:text-red-600 transition-colors"
                  >
                    <FiTrash2 size={14} />
                  </button>
                </span>
              ))}
              {formData.skills.length === 0 && (
                <span className="text-sm text-[var(--text-secondary)]">Belum ada skill</span>
              )}
            </div>
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1">
              Catatan
            </label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows="2"
              placeholder="Catatan tambahan tentang staff"
              className="w-full px-3 py-2 border border-[var(--border-color)] rounded-lg bg-[var(--bg-primary)] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        <div className="flex items-center space-x-4 mt-6">
          <button
            type="submit"
            disabled={loading}
            className="flex items-center space-x-2 px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50"
          >
            <FiSave size={20} />
            <span>{loading ? 'Saving...' : isEdit ? 'Update' : 'Create'} Staff</span>
          </button>
          
          <button
            type="button"
            onClick={() => navigate('/staff')}
            className="px-6 py-2 bg-gray-500 hover:bg-gray-600 text-white font-medium rounded-lg transition-colors"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default StaffForm;